import React, { useState, useRef, useEffect } from "react";
import { MultiStep, StepProps } from '@org/ui';
import { UnitForm, UnitFormRef } from '../forms/unit';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { MultipleSelector } from '@org/ui';
import type { Option } from '@org/ui';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@org/ui';
import { Button } from '@org/ui';
import { Separator } from '@org/ui';
import { Alert, AlertTitle, AlertDescription } from '@org/ui';
import { AlertTriangleIcon } from 'lucide-react';
import { Skeleton } from '@org/ui';
import { unwrapTrpcData } from '@org/ui';
import { useDndSensors } from '../shared/use-dnd-sensors';
import { getMutationErrorMessage, hasOrderChanged } from '../shared/multi-step-utils';
import { SortableItemsCard } from '../shared/sortable-items-card';
import { resolveAfterSave, useStepNavigation } from '../shared/step-navigation';
import { UnsavedChangesDialog } from '../shared/unsaved-changes-dialog';
import { useTranslation } from 'react-i18next';
import { useWomenOptions, WomanThumb } from '../woman/woman-select';

type getClassesForUnitType = RouterOutputs['classes']['getClassesForUnit'][number];

type UnitMultiStepFormProps = {
  defaultValues: any;
  onSubmit: () => void;
  onClose: () => void;
  isEditing: boolean;
};


export const UnitMultiStepForm: React.FC<UnitMultiStepFormProps> = ({
  defaultValues,
  onSubmit,
  onClose,
  isEditing
}) => {
  const { t } = useTranslation('admin');
  const steps: StepProps[] = [
    { title: t('units.form.steps.unitInfo') },
    { title: t('units.form.steps.classes') },
    { title: t('units.form.steps.summary') }
  ];

  const states = ["unit", "classes", "summary"];
  // Creating = wizard (steps unlock as they are saved); editing = free navigation.
  const nav = useStepNavigation(steps.length, isEditing);

  const [unitData, setUnitData] = useState<Record<string, any>>(defaultValues || {});
  const [selectedClasses, setSelectedClasses] = useState<getClassesForUnitType[]>([]);
  // What the server holds for the classes step (loaded, then refreshed after each save).
  const [savedClasses, setSavedClasses] = useState<getClassesForUnitType[]>([]);
  // Unsaved edits on the unit step (react-hook-form `isDirty`); `formKey` remounts it to discard them.
  const [stepDirty, setStepDirty] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // Where the admin wanted to go when the unsaved-changes dialog opened.
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);

  const sensors = useDndSensors();

  // The woman this unit introduces (picked in step 1), for the summary.
  const women = useWomenOptions();
  const selectedWoman = unitData.womanId
    ? women.find((woman) => woman.womanId === unitData.womanId)
    : undefined;

  const formRef = useRef<UnitFormRef>(null);

  const unitId = defaultValues?.unitId;
  const { data: unitClasses, isLoading: isLoadingUnitClasses } =
    trpc.classes.getClassesForUnit.useQuery(unitId, {
      enabled: isEditing && !!unitId
    });

  const saveClassesMutation = trpc.classes.saveClassesForUnit.useMutation({
    onSuccess: () => toast.success(t('units.form.toasts.classesSaved')),
    onError: (error: unknown) => toast.error(t('units.form.toasts.classesSaveFailed', { message: getMutationErrorMessage(error) })),
  });

  const updateClassOrderMutation = trpc.classes.updateClassOrder.useMutation({
    onSuccess: () => toast.success(t('units.form.toasts.classOrderSaved')),
    onError: (error: unknown) => toast.error(t('units.form.toasts.classOrderFailed', { message: getMutationErrorMessage(error) })),
  });

  const utils = trpc.useUtils();
  const handleClassSearch = async (query: string): Promise<Option[]> => {
    if (query.length < 3) return [];
    const results = await utils.classes.searchClasses.fetch({ query });
    return results.map(c => ({
      value: c.classId,
      label: c.name ?? t('units.form.classes.unnamed'),
    }));
  };

  useEffect(() => {
    const unwrapped = unwrapTrpcData<getClassesForUnitType[]>(unitClasses);
    if (unwrapped && unwrapped.length > 0) {
      // Sort by order field if present and add id field
      const sortedClasses = [...unwrapped].sort((a, b) => {
        // If order is undefined, default to 0
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        return orderA - orderB;
      });

      setSelectedClasses(sortedClasses);
      setSavedClasses(sortedClasses);
    }
  }, [unitClasses]);

  /** Classes step differs from what was last saved (selection or order). */
  const classesUnsaved = (): boolean =>
    hasOrderChanged(selectedClasses, savedClasses, (c) => c.classId, (c, i) => c.order ?? i);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setSelectedClasses((items) => {
        const oldIndex = items.findIndex(item => item.classId === active.id);
        const newIndex = items.findIndex(item => item.classId === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex).map((classItem, index) => ({
          ...classItem,
          order: index,
        }));

        return reorderedItems;
      });
    }
  };

  /** Save the classes step when it has unsaved edits, then `onDone` (also when there was nothing to save). */
  const saveClasses = (onDone?: () => void) => {
    if (!unitData.unitId || !classesUnsaved()) {
      onDone?.();
      return;
    }
    const classes = selectedClasses;
    saveClassesMutation.mutate(
      { unitId: unitData.unitId, classes: classes.map((c, index) => ({ classId: c.classId, order: c.order ?? index })) },
      { onSuccess: () => { setSavedClasses(classes); onDone?.(); } },
    );
  };

  const stepHasUnsavedEdits = (index: number): boolean =>
    index === 0 ? stepDirty : index === 1 ? classesUnsaved() : false;

  /** Move; in the wizard the next step unlocks only through `next()`. */
  const moveTo = (target: number) => {
    if (nav.mode === 'wizard' && target === nav.currentIndex + 1) nav.next();
    else nav.goTo(target);
  };

  /** Header / Back click: honour the wizard lock, and never drop unsaved edits silently. */
  const requestStep = (target: number) => {
    if (target === nav.currentIndex || !nav.canReach(target)) return;
    if (stepHasUnsavedEdits(nav.currentIndex)) {
      setPendingTarget(target);
      setUnsavedOpen(true);
      return;
    }
    moveTo(target);
  };

  const handleNext = () => {
    if (nav.currentIndex === 0) {
      setPendingTarget(null);
      formRef.current?.submit(); // → saveUnit moves on
      return;
    }
    if (nav.currentIndex === 1) {
      saveClasses(() => nav.next());
      return;
    }
    nav.next();
  };

  const saveUnit = (data: Record<string, any>) => {
    setUnitData({ ...unitData, ...data });
    setStepDirty(false);
    const target = resolveAfterSave(pendingTarget, nav.currentIndex, steps.length);
    setPendingTarget(null);
    setUnsavedOpen(false);
    moveTo(target);
  };

  const handleUnsavedSave = () => {
    if (nav.currentIndex === 0) {
      formRef.current?.submit(); // → saveUnit closes the dialog and moves
      return;
    }
    const target = pendingTarget;
    saveClasses(() => {
      setUnsavedOpen(false);
      setPendingTarget(null);
      if (target !== null) moveTo(target);
    });
  };

  const handleUnsavedDiscard = () => {
    const target = pendingTarget;
    if (nav.currentIndex === 0) {
      setFormKey((k) => k + 1);
      setStepDirty(false);
    } else if (nav.currentIndex === 1) {
      setSelectedClasses(savedClasses);
    }
    setUnsavedOpen(false);
    setPendingTarget(null);
    if (target !== null) moveTo(target);
  };

  const handleUnsavedCancel = () => {
    setUnsavedOpen(false);
    setPendingTarget(null);
  };

  const handleClassSelectionChange = (options: Option[]) => {
    const newIds = new Set(options.map(o => o.value));
    const currentIds = new Set(selectedClasses.map(c => c.classId));
    // Keep classes that are still selected
    const kept = selectedClasses.filter(c => newIds.has(c.classId));
    // Add newly selected classes
    const added = options
      .filter(o => !currentIds.has(o.value))
      .map((o, idx) => ({
        classId: o.value,
        name: o.label,
        description: null,
        order: kept.length + idx,
      } as unknown as getClassesForUnitType));
    setSelectedClasses([...kept, ...added]);
  };

  const handleClassRemoval = (item: any) => {
    setSelectedClasses(prev => prev.filter(c => c.classId !== item.classId));
  };

  const handleFinalSubmit = () => saveClasses(() => onSubmit());


  return (
    <div className="flex flex-col w-full min-h-screen bg-gray-50">
      <div className="w-full px-8 py-4 bg-white border-b border-gray-200 shadow-sm">
        <MultiStep
          steps={steps}
          currentStep={nav.currentIndex}
          onChange={requestStep}
          mode={nav.mode}
          reachable={nav.reachable}
          labels={{ completed: t('common.steps.completed'), current: t('common.steps.current'), pending: t('common.steps.pending') }}
        />
      </div>

      <div className="w-full flex-grow p-6 max-w-5xl mx-auto mt-6">
        {states[nav.currentIndex] === "unit" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('units.form.unitInfo.title')}</CardTitle>
              <CardDescription>{t('units.form.unitInfo.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <UnitForm
                key={formKey}
                defaultValues={unitData}
                onSubmit={saveUnit}
                onClose={onClose}
                hideButtons={true} // Hide buttons since we're controlling navigation
                onDirtyChange={setStepDirty}
                ref={formRef}
              />
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "classes" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('units.form.classes.title')}</CardTitle>
              <CardDescription>{t('units.form.classes.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingUnitClasses && isEditing ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    {t('units.form.classes.loading')}
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <SortableItemsCard
                    items={selectedClasses}
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    onRemove={handleClassRemoval}
                    idKey="classId"
                    nameKey="name"
                    descriptionKey="description"
                    title={t('units.form.classes.selectedTitle')}
                    emptyTitle={t('units.form.classes.emptyTitle')}
                    emptyDescription={t('units.form.classes.emptyDescription')}
                  />
                  <div className="mt-4">
                    <h3 className="font-medium mb-3 text-gray-800 border-b pb-2">{t('units.form.classes.searchTitle')}</h3>
                    <MultipleSelector
                      onSearch={handleClassSearch}
                      delay={300}
                      value={selectedClasses.map(c => ({ value: c.classId, label: c.name ?? '' }))}
                      onChange={handleClassSelectionChange}
                      placeholder={t('units.form.classes.searchPlaceholder')}
                      emptyIndicator={<p className="text-center text-sm text-muted-foreground">{t('units.form.classes.searchEmpty')}</p>}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "assignments" && (
          <Card>
            <CardHeader>
              <CardTitle>Set Assignments</CardTitle>
              <CardDescription>Configure assignments for this unit.</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedClasses.length > 0 ? (
                <div className="mt-4">
                  <h3 className="font-medium mb-3 text-gray-800 border-b pb-2">Classes in this Unit:</h3>
                  <ol className="list-decimal pl-6 space-y-2">
                    {selectedClasses.map(classItem => (
                      <li key={classItem.classId} className="text-gray-700">
                        <span className="font-medium">{classItem.name}</span>
                        {classItem.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{classItem.description}</p>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                <Alert variant="warning">
                  <AlertTriangleIcon className="h-4 w-4" />
                  <AlertTitle>No classes selected</AlertTitle>
                  <AlertDescription>
                    Please go back and select classes for this unit before configuring assignments.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "summary" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('units.form.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium">{t('units.form.summary.unitInfoTitle')}</CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent className="space-y-2">
                  <p><span className="font-medium text-gray-700">{t('units.form.summary.nameLabel')}</span> {unitData.name}</p>
                  <p><span className="font-medium text-gray-700">{t('units.form.summary.descriptionLabel')}</span> {unitData.description || t('units.form.summary.none')}</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium">{t('units.form.summary.journeyTitle')}</CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">{t('units.form.summary.womanLabel')}</span>
                    {unitData.womanId ? (
                      <>
                        <WomanThumb portraitUrl={selectedWoman?.portraitUrl} className="h-9 w-9" />
                        <span>{selectedWoman?.name ?? t('women.select.unknown')}</span>
                      </>
                    ) : (
                      <span>{t('units.form.summary.none')}</span>
                    )}
                  </div>
                  {unitData.empowermentPoints !== undefined && unitData.empowermentPoints !== null && (
                    <p><span className="font-medium text-gray-700">{t('units.form.summary.pointsLabel')}</span> {unitData.empowermentPoints}</p>
                  )}
                </CardContent>
              </Card>

              {selectedClasses.length > 0 && (
                <Card className="bg-gray-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium">{t('units.form.summary.classesTitle', { count: selectedClasses.length })}</CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal pl-6 space-y-2">
                      {selectedClasses.map(classItem => (
                        <li key={classItem.classId} className="text-gray-700">
                          <span className="font-medium">{classItem.name}</span>
                          {classItem.description && (
                            <p className="text-sm text-gray-500 mt-0.5">{classItem.description}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="w-full py-4 px-6 flex justify-between bg-white border-t border-gray-200 shadow-sm mt-auto">
        {nav.currentIndex > 0 && (
          <Button
            variant="outline"
            onClick={() => requestStep(nav.currentIndex - 1)}
          >
            {t('units.form.actions.back')}
          </Button>
        )}
        <div className="ml-auto">
          {!nav.isLast ? (
            <Button
              variant="outline"
              onClick={handleNext}
            >
              {t('units.form.actions.next')}
            </Button>
          ) : (
            <Button
              onClick={handleFinalSubmit}
              variant="outline"
            >
              {t('units.form.actions.saveFinish')}
            </Button>
          )}
        </div>
      </div>

      <UnsavedChangesDialog
        open={unsavedOpen}
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={handleUnsavedCancel}
        saving={saveClassesMutation.isPending}
      />
    </div>
  );
};
